import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { ApplicationState } from 'store';
import { AiManagementColumn, AiManagementState } from 'store/modules/ai_management_store/types';
import {
  getColumns,
  getCards,
  getTeam,
  reorderColumns,
  renameColumnLocal,
  recolorColumnLocal,
  removeColumnLocal,
  moveCardLocal,
} from 'store/modules/ai_management_store/actions';
import {
  createColumn,
  renameColumn,
  recolorColumn,
  deleteColumn,
  reorderColumn,
} from 'services/actions/ai_management_columns';
import { archiveCard, moveCard } from 'services/actions/ai_management_cards';
import AiManagementKanbanColumn from 'components/molecules/ai_management_kanban_column';
import AiManagementColumnColorModal from 'components/molecules/ai_management_column_color_modal';
import AiManagementDeleteColumnModal, {
  DeleteColumnChoice,
} from 'components/molecules/ai_management_delete_column_modal';
import { Board } from './styles';

const BOTTOM_GAP = 12;
const MIN_BOARD_HEIGHT = 320;
const POSITION_STEP = 1000;
const DEFAULT_NEW_COLUMN_COLOR = '#3949AB';

const AiManagementKanban: React.FC = () => {
  const dispatch = useDispatch();
  const { columns, cards, filters, request } = useSelector<ApplicationState, AiManagementState>(
    (state) => state.session.ai_management,
  );
  const boardRef = useRef<HTMLDivElement | null>(null);
  const createInputRef = useRef<HTMLInputElement>(null);
  const [boardHeight, setBoardHeight] = useState<number | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [colorColumn, setColorColumn] = useState<AiManagementColumn | null>(null);
  const [deletingColumn, setDeletingColumn] = useState<AiManagementColumn | null>(null);

  useEffect(() => {
    dispatch(getTeam());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getColumns(filters));
    dispatch(getCards(filters));
  }, [dispatch, filters]);

  const updateHeight = useCallback(() => {
    if (!boardRef.current) return;
    const { top } = boardRef.current.getBoundingClientRect();
    const available = Math.round(window.innerHeight - top - BOTTOM_GAP);
    setBoardHeight(Math.max(available, MIN_BOARD_HEIGHT));
  }, []);

  useEffect(() => {
    if (!request.columns.loaded) return undefined;

    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.addEventListener('orientationchange', updateHeight);
    const observer = new ResizeObserver(updateHeight);
    observer.observe(document.body);

    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
      observer.disconnect();
    };
  }, [request.columns.loaded, updateHeight]);

  useEffect(() => {
    if (creating) createInputRef.current?.focus();
  }, [creating]);

  const orderedColumns = useMemo(
    () => [...columns].sort((a, b) => a.sort_group - b.sort_group || a.position - b.position),
    [columns],
  );

  const handleCardDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const fromColumnId = Number(source.droppableId.split(':')[1]);
    const toColumnId = Number(destination.droppableId.split(':')[1]);
    const toIndex = destination.index;
    if (fromColumnId === toColumnId && source.index === toIndex) return;

    const cardId = Number(draggableId.split(':')[1]);

    const destWithoutMoved =
      fromColumnId === toColumnId
        ? (cards[fromColumnId] ?? []).filter((card) => card.id !== cardId)
        : cards[toColumnId] ?? [];
    const beforeCard = toIndex > 0 ? destWithoutMoved[toIndex - 1] : undefined;
    const afterCardId = beforeCard ? beforeCard.id : null;

    dispatch(moveCardLocal({ cardId, fromColumnId, toColumnId, toIndex }));
    moveCard(cardId, toColumnId, afterCardId).then((res) => {
      if (!res || res.status >= 400) dispatch(getCards(filters));
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (result.type === 'CARD') {
      handleCardDragEnd(result);
      return;
    }

    const { source, destination } = result;
    if (!destination || destination.index === source.index) return;

    const reorderableIndexes = orderedColumns
      .map((col, index) => (col.permissions.reorderable ? index : -1))
      .filter((index) => index >= 0);
    if (reorderableIndexes.length === 0) return;

    const bandStart = reorderableIndexes[0];
    const bandEnd = reorderableIndexes[reorderableIndexes.length - 1];
    const target = Math.min(Math.max(destination.index, bandStart), bandEnd);
    if (target === source.index) return;

    const next = [...orderedColumns];
    const [moved] = next.splice(source.index, 1);
    next.splice(target, 0, moved);

    let order = 0;
    const repositioned = next.map((col) => {
      if (!col.permissions.reorderable) return col;
      order += 1;
      return { ...col, position: order * POSITION_STEP };
    });

    dispatch(reorderColumns(repositioned));

    const afterColumnId = next[target - 1].id;
    reorderColumn(moved.id, afterColumnId).then((res) => {
      if (!res || res.status >= 400) dispatch(getColumns(filters));
    });
  };

  const handleRename = (id: number, name: string) => {
    dispatch(renameColumnLocal(id, name));
    renameColumn(id, name).then((res) => {
      if (!res || res.status >= 400) dispatch(getColumns(filters));
    });
  };

  const handleRecolor = (id: number, color: string) => {
    dispatch(recolorColumnLocal(id, color));
    recolorColumn(id, color).then((res) => {
      if (!res || res.status >= 400) dispatch(getColumns(filters));
    });
  };

  const removeColumn = (column: AiManagementColumn, targetColumnId?: number) => {
    dispatch(removeColumnLocal(column.id));
    deleteColumn(column.id, { targetColumnId }).then((res) => {
      if (res && res.status < 400) {
        dispatch(getCards(filters));
        return;
      }
      dispatch(getColumns(filters));

      if (res?.status === 422 && res.data?.cards_count) {
        setDeletingColumn({ ...column, cards_count: res.data.cards_count });
      }
    });
  };

  const handleDeleteConfirm = (choice: DeleteColumnChoice) => {
    if (!deletingColumn) return;
    const column = deletingColumn;
    setDeletingColumn(null);

    if (choice.archiveCards) {
      const columnCards = cards[column.id] ?? [];
      Promise.all(columnCards.map((card) => archiveCard(card.id))).then(() => removeColumn(column));
      return;
    }

    removeColumn(column, choice.targetColumnId);
  };

  const getAfterColumnIdForNew = (): number | undefined => {
    for (let i = orderedColumns.length - 1; i >= 0; i -= 1) {
      if (orderedColumns[i].permissions.reorderable) return orderedColumns[i].id;
    }
    return orderedColumns[orderedColumns.length - 2]?.id;
  };

  const handleCreate = () => {
    const trimmed = newColumnName.trim();
    if (!trimmed) {
      setCreating(false);
      return;
    }
    createColumn({
      name: trimmed,
      color: DEFAULT_NEW_COLUMN_COLOR,
      after_column_id: getAfterColumnIdForNew(),
    }).then((res) => {
      if (res && res.status < 400) dispatch(getColumns(filters));
    });
    setNewColumnName('');
    setCreating(false);
  };

  const cancelCreate = () => {
    setNewColumnName('');
    setCreating(false);
  };

  if (!request.columns.loaded && !request.columns.error) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  if (request.columns.error) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-2">Não foi possível carregar as colunas.</p>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => dispatch(getColumns(filters))}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="ai-management-board" direction="horizontal" type="COLUMN">
          {(dropProvided) => (
            <Board
              ref={(el: HTMLDivElement | null) => {
                dropProvided.innerRef(el);
                boardRef.current = el;
              }}
              boardHeight={boardHeight}
              {...dropProvided.droppableProps}
              className="d-flex overflow-auto pb-2"
            >
              {orderedColumns.map((column, index) => (
                <Draggable
                  key={column.id}
                  draggableId={String(column.id)}
                  index={index}
                  isDragDisabled={!column.permissions.reorderable}
                >
                  {(dragProvided, snapshot) => (
                    <AiManagementKanbanColumn
                      column={column}
                      cards={cards[column.id] ?? []}
                      provided={dragProvided}
                      isDragging={snapshot.isDragging}
                      onRename={handleRename}
                      onRecolor={setColorColumn}
                      onDelete={setDeletingColumn}
                    />
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}

              {creating ? (
                <div className="board-add-slot flex-shrink-0 align-self-start">
                  <input
                    ref={createInputRef}
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Nome da coluna (Enter para criar)"
                    value={newColumnName}
                    onChange={(event) => setNewColumnName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleCreate();
                      if (event.key === 'Escape') cancelCreate();
                    }}
                    onBlur={cancelCreate}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-light border text-muted d-flex align-items-center justify-content-center flex-shrink-0 align-self-start board-add-slot"
                  onClick={() => setCreating(true)}
                >
                  <i className="material-icons mr-1">add</i>
                  Adicionar coluna
                </button>
              )}
            </Board>
          )}
        </Droppable>
      </DragDropContext>

      <AiManagementColumnColorModal
        column={colorColumn}
        show={!!colorColumn}
        onClose={() => setColorColumn(null)}
        onSave={handleRecolor}
      />

      <AiManagementDeleteColumnModal
        column={deletingColumn}
        columns={orderedColumns}
        show={!!deletingColumn}
        onClose={() => setDeletingColumn(null)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default AiManagementKanban;
